import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {Subject} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {NameValueDTO} from 'src/app/modules/common/models/NameValueDTO';
import {HelpDTO} from 'src/app/modules/other/models/HelpDTO';
import {HelpService} from 'src/app/modules/other/services/help.service';
import {ErrorDialogComponent} from 'src/app/modules/common/dialogs/error-dialog/error-dialog.component';
import {Common} from 'src/app/modules/common/util/Util';
import {LookupService} from 'src/app/modules/common/services/lookup.service';
import {KeycloakService} from 'keycloak-angular';
import {DisplayStrings} from 'src/app/modules/common/constants/DisplayStrings';
import {AddressesArrayDTO} from '../../../../ent/models/AddressesArrayDTO';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {AddressDTO} from '../../../../ent/models/AddressDTO';

export function minimumAddressesValidator(addresses: AddressesArrayDTO[]): ValidatorFn {
  return (): { [key: string]: any } | null => {
    return addresses && addresses.length > 0 ? null : {minimumAddresses: true};
  };
}

@Component({
  selector: 'greydom-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: [
    './address-form.component.scss',
    '../../../../../../styles/global/_add-view-edit-styles.scss'
  ]
})
export class AddressFormComponent implements OnChanges, OnInit, AfterViewChecked, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly lookupValue = LookupValue;
  readonly listFormRowHeight = '60px';

  @Input() data: { addresses: AddressesArrayDTO[]; };
  @Input() formGroup: FormGroup;

  addressType: FormControl = new FormControl();
  countryFormControl: FormControl = new FormControl();
  addressLineOneFormControl: FormControl = new FormControl();
  cityTownFormControl: FormControl = new FormControl();
  addressLine2FormControl: FormControl = new FormControl();
  postalCodeFormControl: FormControl = new FormControl();
  doNotOverwriteAddress: FormControl = new FormControl(); // since same component can't have same name 'doNotOverride' for address/contact
  provinceFormControl: FormControl = new FormControl();

  address: AddressDTO;
  addressFormGroup: FormGroup;
  selectedAddress = null;
  selectedAddresses: AddressesArrayDTO[];
  addressErrorMessage: string = null;
  addressErrorMessagesList: { [key: string]: string };
  addressRequiredFieldsSelected = true;

  // Lookup values
  addressTypes = [];
  countries = [];

  helpInfo: HelpDTO[];
  userFirstLastName = null;

  constructor(
    private changeDetect: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private helpService: HelpService,
    private lookupService: LookupService,
    private keycloakService: KeycloakService,
  ) {
    this.selectedAddresses = [];
    this.addressErrorMessagesList = DisplayStrings.addressErrorMessagesList;
    this.addressErrorMessage = this.addressErrorMessagesList.minAddressError;

    // FormGroups:
    this.addressFormGroup = this.formBuilder.group({
      type: this.addressType,
      country: this.countryFormControl,
      province: this.provinceFormControl,
      cityTown: this.cityTownFormControl,
      addressLine1: this.addressLineOneFormControl,
      addressLine2: this.addressLine2FormControl,
      postalCode: this.postalCodeFormControl,
      doNotOverwrite: this.doNotOverwriteAddress
    }, {validator: [minimumAddressesValidator(this.selectedAddresses)]});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      this.setFormsForEdit(this.data);
    }

  }

  ngOnInit(): void {
    this.keycloakService.loadUserProfile().then((profile) => {
      this.userFirstLastName = `${profile.firstName} ${profile.lastName}`;
    });
    this.getListOfLookupValues(this.lookupValue.AddressType);
    this.getListOfLookupValues(this.lookupValue.Country);
  }

  ngAfterViewChecked(): void {
    this.changeDetect.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }


  getHelpInfo(componentName: string) {
    this.helpService.getListOfHelpInfo(componentName).subscribe(
      (response) => {
        this.helpInfo = response;
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.handleError(error.businessMessage);
      }
    );
  }

  setFormsForEdit(data) {
    if (data.addresses && data.addresses.length) {
      this.selectedAddresses = data.addresses.map(element => {
        const addressElement: AddressesArrayDTO = {
          ...element,
          updated: new Date(),
          updatedBy: this.userFirstLastName,
          created: element.created || new Date(),
          createdBy: element.createdBy || this.userFirstLastName
        };


        const addressData = element.address || element.addressDTO;
        if (addressData) {
          addressElement.address = {
            ...addressData,
            updated: new Date(),
            updatedBy: this.userFirstLastName,
            created: addressData.created || new Date(),
            createdBy: addressData.createdBy || this.userFirstLastName
          };
          addressElement.addressDTO = addressElement.address; // Ensure both properties are set
        }

        return addressElement;
      });
    }
    this.addressFormGroup = this.formBuilder.group({
      type: this.addressType,
      country: this.countryFormControl,
      province: this.provinceFormControl,
      cityTown: this.cityTownFormControl,
      addressLine1: this.addressLineOneFormControl,
      addressLine2: this.addressLine2FormControl,
      postalCode: this.postalCodeFormControl,
      doNotOverwrite: this.doNotOverwriteAddress
    }, {validator: [minimumAddressesValidator(this.selectedAddresses)]});
  }

  getListOfLookupValues(entity: string): void {
    this.lookupService.getListOfLookupValues(entity).subscribe(
      (response: NameValueDTO[]) => {
        if (entity === this.lookupValue.AddressType) {
          this.addressTypes = response;
        }
        else if (entity === this.lookupValue.Country) {
          this.countries = response;
        }
      },
      (errorResponse) => {
        const error = errorResponse.error;
        this.handleError(error.businessMessage);
      }
    );
  }

  getInfoTooltip(inputName: string): string {
    let message = '';
    if (this.helpInfo) {
      const helpInfo = this.helpInfo.find((help) => help.inputName === inputName);
      message = helpInfo ? helpInfo.inputInfo : '';
    }
    return message;
  }


  getAddressDisplayString(addressElement: AddressesArrayDTO): string {
    if (!addressElement) {
      return '';
    }
    const address: AddressDTO = this.getAddressFromElement(addressElement);
    if (!address) {
      return '';
    }
    return `${address.type?.name} - ${address.addressLineOne} - ${address.cityTown} - ${address.province} -
    ${address.country?.name} - ${address.postalCode}`;
  }


  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

  handleError(errorMessage: string) {
    this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage
    });
  }


  addressSelected() {
    if (this.addressType.value != null && this.countryFormControl.value != null
      && this.provinceFormControl.value != null && this.cityTownFormControl.value != null
      && this.addressLineOneFormControl.value != null && this.postalCodeFormControl.value != null) {
      const findAddress = this.selectedAddresses.find(item => item.address.type.value === this.addressType.value.value);
      if (findAddress) {
        this.addressRequiredFieldsSelected = false;
        this.addressErrorMessage = this.addressErrorMessagesList.addressTypeError;
        return;
      }
      this.addressRequiredFieldsSelected = true;
      this.address = {
        type: this.addressType.value,
        country: this.countryFormControl.value,
        province: this.provinceFormControl.value,
        cityTown: this.cityTownFormControl.value,
        addressLineOne: this.addressLineOneFormControl.value,
        addressLineTwo: this.addressLine2FormControl.value,
        postalCode: this.postalCodeFormControl.value,
        addressId: null,
        created: new Date(),
        createdBy: this.userFirstLastName,
        doNotOverwrite: !!this.doNotOverwriteAddress.value ? 1 : 0,
        latitude: null,
        location: null,
        longitude: null,
        primaryIndicator: null,
        suburb: null,
        tenantId: null,
        updated: null,
        updatedBy: null
      };
      const addressLink: AddressesArrayDTO = {
        address: this.address,
        created: new Date(),
        createdBy: this.userFirstLastName,
        startDate: new Date('1970-01-01'),  //  See PR: GD-524
        endDate: null,
        tenantId: null,
        updated: null,
        updatedBy: null
      };
      if (addressLink) {
        this.selectedAddress = null;
        this.selectedAddresses.push(addressLink);
        this.clearAddressFields();
      }
    }
    else {
      this.addressErrorMessage = this.addressErrorMessagesList.addressMandatoryError;
      this.addressRequiredFieldsSelected = false;
    }
  }
  editAddress(addressElement: AddressesArrayDTO) {
    if (addressElement) {
      if (this.selectedAddress) {
        this.selectedAddresses.push(this.selectedAddress);
        this.selectedAddress = null;
      }
      this.selectedAddress = addressElement;
      const address = addressElement.address || addressElement.addressDTO;
      if (address) {
        const type = this.addressTypes.find(item => item.value === address.type.value);
        const country = this.countries.find(item => item.value === address.country.value);
        this.addressType.setValue(type);
        this.countryFormControl.setValue(country);
        this.provinceFormControl.setValue(address.province);
        this.cityTownFormControl.setValue(address.cityTown);
        this.addressLineOneFormControl.setValue(address.addressLineOne);
        this.addressLine2FormControl.setValue(address.addressLineTwo);
        this.postalCodeFormControl.setValue(address.postalCode);
        this.doNotOverwriteAddress.setValue(!!address.doNotOverwrite);
        this.selectedAddresses = this.selectedAddresses.filter(item => {
          const itemAddress = this.getAddressFromElement(item);
          return itemAddress.type.value !== address.type.value;
        });
      }
      if (this.selectedAddresses.length === 0) {
        this.addressFormGroup = this.formBuilder.group({
          type: this.addressType,
          country: this.countryFormControl,
          province: this.provinceFormControl,
          cityTown: this.cityTownFormControl,
          addressLine1: this.addressLineOneFormControl,
          addressLine2: this.addressLine2FormControl,
          postalCode: this.postalCodeFormControl,
          doNotOverwrite: this.doNotOverwriteAddress
        }, {validator: [minimumAddressesValidator(this.selectedAddresses)]});
      }
    }

  }

  clearAddressFields() {
    this.addressFormGroup.reset();
  }


  resetAddressFormGroup() {
    this.addressFormGroup.reset();
  }

  removeAddress(address) {
    const index = this.selectedAddresses.indexOf(address, 0);
    if (index > -1) {
      this.selectedAddresses.splice(index, 1);
    }
    if (this.selectedAddresses.length === 0) {
      this.addressFormGroup = this.formBuilder.group({
        type: this.addressType,
        country: this.countryFormControl,
        province: this.provinceFormControl,
        cityTown: this.cityTownFormControl,
        addressLine1: this.addressLineOneFormControl,
        addressLine2: this.addressLine2FormControl,
        postalCode: this.postalCodeFormControl
      }, {validator: [minimumAddressesValidator(this.selectedAddresses)]});
      this.addressErrorMessage = this.addressErrorMessagesList.minAddressError;
    }
  }

  // Custom Methods to Access Via ViewChild:
  getPayload(): { addresses: AddressesArrayDTO[] } {
    return {
      addresses: this.selectedAddresses?.length ? this.selectedAddresses.map(addressElement => {
        const { addressDTO, ...rest } = addressElement as AddressesArrayDTO;
        return {
          ...rest,
          address: this.getAddressFromElement(addressElement)
        };
      }) : []
    };
  }

  getPayloadAsDTO(): { addresses: AddressesArrayDTO[] } {
    // Backend accepts as addressDTO rather than address  see getPayload Method
    // Needs to fix BE: PHG and Broker both inorder to utilise getPayload.

    return {
      addresses: this.selectedAddresses?.length ? this.selectedAddresses.map(addressElement => {
        const { address, ...rest } = addressElement as AddressesArrayDTO;
        return {
          ...rest,
          addressDTO: this.getAddressFromElement(addressElement)
        };
      }) : []
    };
  }

  private getAddressFromElement(addressElement: AddressesArrayDTO): AddressDTO {
    return addressElement.address || addressElement.addressDTO;
  }


}
